import { useMemo } from 'react';
import { HighlightedCode, type HljsLanguage } from './HighlightedCode';

type DiffLine =
  | { type: 'same'; text: string; beforeNum: number; afterNum: number }
  | { type: 'add'; text: string; afterNum: number }
  | { type: 'remove'; text: string; beforeNum: number };

type DiffRow = [DiffLine | null, DiffLine | null];

const MAX_DIFF_LINES = 4000;

function lineDiff(before: string, after: string): DiffLine[] {
  const beforeLines = before.split('\n');
  const afterLines = after.split('\n');
  const m = beforeLines.length;
  const n = afterLines.length;

  if (m > MAX_DIFF_LINES || n > MAX_DIFF_LINES) {
    const removes: DiffLine[] = beforeLines.map((text, i) => ({ type: 'remove', text, beforeNum: i + 1 }));
    const adds: DiffLine[] = afterLines.map((text, i) => ({ type: 'add', text, afterNum: i + 1 }));
    return [...removes, ...adds];
  }

  const lcs: Uint32Array[] = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (beforeLines[i - 1] === afterLines[j - 1]) {
        lcs[i][j] = lcs[i - 1][j - 1] + 1;
      } else {
        lcs[i][j] = Math.max(lcs[i - 1][j], lcs[i][j - 1]);
      }
    }
  }

  const result: DiffLine[] = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && beforeLines[i - 1] === afterLines[j - 1]) {
      result.unshift({ type: 'same', text: beforeLines[i - 1], beforeNum: i, afterNum: j });
      i--; j--;
    } else if (j > 0 && (i === 0 || lcs[i][j - 1] >= lcs[i - 1][j])) {
      result.unshift({ type: 'add', text: afterLines[j - 1], afterNum: j });
      j--;
    } else {
      result.unshift({ type: 'remove', text: beforeLines[i - 1], beforeNum: i });
      i--;
    }
  }
  return result;
}

function toSideBySideRows(diff: DiffLine[]): DiffRow[] {
  const rows: DiffRow[] = [];
  let i = 0;
  while (i < diff.length) {
    if (diff[i].type === 'same') {
      rows.push([diff[i], diff[i]]);
      i++;
      continue;
    }
    const removes: DiffLine[] = [];
    const adds: DiffLine[] = [];
    while (i < diff.length && diff[i].type === 'remove') { removes.push(diff[i]); i++; }
    while (i < diff.length && diff[i].type === 'add') { adds.push(diff[i]); i++; }
    const maxLen = Math.max(removes.length, adds.length);
    for (let k = 0; k < maxLen; k++) {
      rows.push([removes[k] ?? null, adds[k] ?? null]);
    }
  }
  return rows;
}

function lineClass(type: DiffLine['type'] | 'empty'): string {
  switch (type) {
    case 'add': return 'file-diff-row add';
    case 'remove': return 'file-diff-row remove';
    case 'same': return 'file-diff-row same';
    default: return 'file-diff-row empty';
  }
}

function lineSign(type: DiffLine['type'] | 'empty'): string {
  if (type === 'add') return '+';
  if (type === 'remove') return '-';
  return ' ';
}

export function FileDiffView({
  before,
  after,
  path,
  language,
  isNewFile,
}: {
  before: string;
  after: string;
  path?: string;
  language: HljsLanguage;
  isNewFile?: boolean;
}) {
  const diff = useMemo(() => (isNewFile ? [] : lineDiff(before, after)), [before, after, isNewFile]);

  const hasAdd = diff.some(d => d.type === 'add');
  const hasRemove = diff.some(d => d.type === 'remove');

  if (isNewFile) {
    return (
      <div className="file-diff file-diff-new">
        <HighlightedCode
          code={after}
          language={language}
          filename={path}
          className="tool-detail-code"
        />
      </div>
    );
  }

  if (!hasAdd && !hasRemove) {
    return (
      <div className="file-diff file-diff-unchanged">
        <div className="file-diff-empty-note">No textual changes detected.</div>
        <HighlightedCode
          code={after}
          language={language}
          filename={path}
          className="tool-detail-code"
        />
      </div>
    );
  }

  if (hasRemove && !hasAdd) {
    const lines = diff.filter((d): d is Exclude<DiffLine, { type: 'add' }> => d.type !== 'add');
    return (
      <div className="file-diff file-diff-remove-only">
        <div className="file-diff-col">
          {lines.map((line, idx) => (
            <div className={lineClass(line.type)} key={idx}>
              <span className="file-diff-ln">{line.beforeNum}</span>
              <span className="file-diff-sign">{lineSign(line.type)}</span>
              <span className="file-diff-text">{line.text || ' '}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (hasAdd && !hasRemove) {
    const lines = diff.filter((d): d is Exclude<DiffLine, { type: 'remove' }> => d.type !== 'remove');
    return (
      <div className="file-diff file-diff-add-only">
        <div className="file-diff-col">
          {lines.map((line, idx) => (
            <div className={lineClass(line.type)} key={idx}>
              <span className="file-diff-ln">{line.afterNum}</span>
              <span className="file-diff-sign">{lineSign(line.type)}</span>
              <span className="file-diff-text">{line.text || ' '}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const rows = toSideBySideRows(diff);
  return (
    <div className="file-diff file-diff-side-by-side">
      <div className="file-diff-col file-diff-col-before">
        {rows.map(([left], idx) => {
          const type = left?.type ?? 'empty';
          return (
            <div className={lineClass(type)} key={idx}>
              <span className="file-diff-ln">{left && left.type !== 'add' ? left.beforeNum : ''}</span>
              <span className="file-diff-sign">{type === 'remove' ? '-' : ' '}</span>
              <span className="file-diff-text">{left ? (left.text || ' ') : ' '}</span>
            </div>
          );
        })}
      </div>
      <div className="file-diff-col file-diff-col-after">
        {rows.map(([, right], idx) => {
          const type = right?.type ?? 'empty';
          return (
            <div className={lineClass(type)} key={idx}>
              <span className="file-diff-ln">{right && right.type !== 'remove' ? right.afterNum : ''}</span>
              <span className="file-diff-sign">{type === 'add' ? '+' : ' '}</span>
              <span className="file-diff-text">{right ? (right.text || ' ') : ' '}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
